// The Jobs Room — chord-hearing reps. A sit is eight chords: each one
// sounds (all its notes at once), the learner commits to a job — home,
// away, or tension — and the reveal answers warmly with the chord's
// persona word and, for minor and raised entries, which side it lives on
// ("that was the minor side's 4"). The end card sums the sit in words,
// never percentages. The pool grows with the course (jobsPool), and the
// round order comes shuffled with no immediate repeats (makeJobsRound).

import React, { useEffect, useRef, useState } from "react";

import { PATCHES, playPhrase, resumeAudio } from "../../../lib/homeKeyAudio";
import { jobsPool, makeJobsRound } from "../../../lib/homeKeyPractice";

const SIT_LENGTH = 8;
const CHORD_MS = 1600;
const JOBS = ["home", "away", "tension"];

// The entry's three (or so) midis as one chord — simultaneous notes.
const chordOf = (entry) => ({
  notes: (entry.midis || []).map((midi) => ({ midi, at: 0, dur: CHORD_MS })),
});

// Who that was — persona word (the entry's label) and side included, so
// minor and raised entries read "the minor side's 4" after the reveal.
const entryName = (entry) => {
  const degree = entry.kind === "raised" ? "5♯" : entry.degree;
  if (entry.side === "minor") {
    return `the minor side's ${degree} — “${entry.label}”`;
  }
  return `the ${degree} chord — “${entry.label}”`;
};

// A listening hint per job, for when the ear picked differently.
const JOB_HINTS = {
  home: "home chords sit all the way down — no lean left in them.",
  away: "away chords hover — off the ground, but not itchy yet.",
  tension: "tension chords lean hard toward home — listen for the itch.",
};

const NUMBER_WORDS = [
  "none",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
];

export default function JobsRoom({ completed, onExit }) {
  const [phase, setPhase] = useState("ready"); // ready | listen | reveal | end
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState(null);
  const [results, setResults] = useState([]); // { picked, job } per chord

  const roundRef = useRef([]);
  const playHandle = useRef(null);

  const stopChord = () => {
    if (playHandle.current && playHandle.current.stop) playHandle.current.stop();
    playHandle.current = null;
  };
  useEffect(() => () => stopChord(), []);

  const playEntry = (i) => {
    resumeAudio();
    stopChord();
    const entry = roundRef.current[i];
    if (!entry) return;
    playHandle.current = playPhrase(chordOf(entry), PATCHES.softKeys);
  };

  const startSit = () => {
    roundRef.current = makeJobsRound(jobsPool(completed), {
      length: SIT_LENGTH,
    });
    setIndex(0);
    setPicked(null);
    setResults([]);
    setPhase("listen");
    playEntry(0);
  };

  const commit = (job) => {
    if (phase !== "listen") return;
    const entry = roundRef.current[index];
    setPicked(job);
    setResults((rs) => [...rs, { picked: job, job: entry.job }]);
    setPhase("reveal");
  };

  const next = () => {
    stopChord();
    if (index >= roundRef.current.length - 1) {
      setPhase("end");
      return;
    }
    const i = index + 1;
    setIndex(i);
    setPicked(null);
    setPhase("listen");
    playEntry(i);
  };

  // ----- copy -----

  const entry = roundRef.current[index];
  const hit = entry && picked === entry.job;

  const revealLine = () => {
    if (!entry) return "";
    return hit
      ? `${entry.job} — that's the one. You heard ${entryName(entry)}.`
      : `that one was doing the ${entry.job} job — it was ${entryName(entry)}.`;
  };

  const endLines = () => {
    const total = results.length;
    const firstTry = results.filter((r) => r.picked === r.job).length;
    let line;
    if (firstTry === total && total > 0) {
      line = "all eight, straight off the ear. The jobs are yours.";
    } else if (firstTry >= 6) {
      line = `${NUMBER_WORDS[firstTry]} of the eight landed on the first listen — the jobs are settling in.`;
    } else if (firstTry >= 3) {
      line = `${NUMBER_WORDS[firstTry]} of the eight landed on the first listen — the ear is finding its feet.`;
    } else {
      line =
        "a slippery sit — but every listen tunes the ear. The jobs speak up a little more each visit.";
    }
    // Name the job that slipped away most, when one clearly did.
    const missed = {};
    results.forEach((r) => {
      if (r.picked !== r.job) missed[r.job] = (missed[r.job] || 0) + 1;
    });
    const slippery = JOBS.filter((j) => (missed[j] || 0) >= 2).sort(
      (a, b) => missed[b] - missed[a]
    )[0];
    return { line, slippery };
  };

  // ----- render -----

  return (
    <div className="hk-moment-body">
      {phase === "ready" && (
        <>
          <p>
            Eight chords, one at a time. Each one is doing a job — settled at
            home, hovering away, or leaning with tension. Hear it, commit,
            then we'll say who it was.
          </p>
          <div className="hk-choice-row">
            <button
              type="button"
              className="hk-btn hk-btn--primary"
              onClick={startSit}
            >
              ▶ start the sit
            </button>
          </div>
        </>
      )}

      {(phase === "listen" || phase === "reveal") && (
        <>
          <p className="hk-echo-status">
            chord {index + 1} of {roundRef.current.length}
            {phase === "listen" && " — where does it sit?"}
          </p>

          {phase === "reveal" && (
            <>
              <p className="hk-echo-status">{revealLine()}</p>
              {!hit && <p className="hk-echo-hint">{JOB_HINTS[entry.job]}</p>}
            </>
          )}

          <div
            className="hk-choice-row"
            role="group"
            aria-label="commit to a job"
          >
            {JOBS.map((job) => (
              <button
                key={job}
                type="button"
                className="hk-choice"
                aria-pressed={
                  phase === "reveal" ? Boolean(entry && job === entry.job) : false
                }
                disabled={phase === "reveal"}
                onClick={() => commit(job)}
              >
                {job}
              </button>
            ))}
          </div>

          <div className="hk-choice-row">
            <button
              type="button"
              className="hk-demo-btn"
              onClick={() => playEntry(index)}
            >
              ▶ hear it again
            </button>
            {phase === "reveal" && (
              <button
                type="button"
                className="hk-btn hk-btn--primary"
                onClick={next}
              >
                {index >= roundRef.current.length - 1
                  ? "how did it go?"
                  : "next"}
              </button>
            )}
          </div>
        </>
      )}

      {phase === "end" && (
        <>
          <p className="hk-echo-status">{endLines().line}</p>
          {endLines().slippery && (
            <p className="hk-echo-hint">
              {endLines().slippery} was the slippery one today —{" "}
              {JOB_HINTS[endLines().slippery]}
            </p>
          )}
          <div className="hk-choice-row">
            <button
              type="button"
              className="hk-btn hk-btn--primary"
              onClick={startSit}
            >
              another sit
            </button>
            <button
              type="button"
              className="hk-btn hk-btn--ghost"
              onClick={onExit}
            >
              back to the hall
            </button>
          </div>
        </>
      )}
    </div>
  );
}
